import { message as antdMessage, Button, Card, Col, Form, Input, Row, Typography } from "antd"
import { useEffect, useState } from "react"
import { AddressInput, ContainerFull } from "../../components"
import { getProfile, updateUser } from "../../endpoints/userEndpoints"
import { AddressInterface, BillingAddressInterface } from "../../types"

const { Title } = Typography

export const Profile = (): JSX.Element => {
    const [form] = Form.useForm()
    const [saving, setSaving] = useState(false)
    const [address, setAddress] = useState<AddressInterface>()
    const [billingAddress, setBillingAddress] = useState<BillingAddressInterface>()
    const [messageApi, contextHolder] = antdMessage.useMessage()

    useEffect(() => {
        let ignore = false
        getProfile()
            .then((data) => {
                if (ignore) return
                if (data.code === 1000 && data.user) {
                    form.setFieldsValue({
                        firstName: data.user.firstName,
                        lastName: data.user.lastName,
                        email: data.user.email,
                        institution: data.user.institution || "",
                        phone: data.user.phone || "",
                        cell: data.user.cell || "",
                    })

                    setAddress({
                        line1: data.user.address?.line1 || "",
                        line2: data.user.address?.line2 || "",
                        city: data.user.address?.city || "",
                        state: data.user.address?.state || "",
                        country: data.user.address?.country || "",
                        zip: data.user.address?.zip || "",
                    })

                    setBillingAddress({
                        line1: data.user.billingAddress?.line1 || "",
                        line2: data.user.billingAddress?.line2 || "",
                        city: data.user.billingAddress?.city || "",
                        state: data.user.billingAddress?.state || "",
                        country: data.user.billingAddress?.country || "",
                        zip: data.user.billingAddress?.zip || "",
                        billingContactName: data.user.billingAddress?.billingContactName || "",
                        billingEmail: data.user.billingAddress?.billingEmail || "",
                    })
                }
            })
            .catch(() => {
                // Handle error silently
            })
        return () => {
            ignore = true
        }
    }, [form])
    const onFinish = async (values: any) => {
        setSaving(true)
        const userPayload = {
            ...values,
            address: address,
            billingAddress: billingAddress,
        }
        const res = await updateUser({ user: userPayload })
        setSaving(false)
        if (res.code === 1000) {
            messageApi.success("Profile updated")
        } else {
            messageApi.error(res.message || "Failed to update profile")
        }
    }

    // if (loading) return <Skeleton /> //this is causing warning Warning: Instance created by useForm is not connected to any Form element. Forget to pass form prop?

    return (
        <ContainerFull style={{ fontFamily: '"Rubik", sans-serif', padding: '24px' }}>
            {contextHolder}
            <Title level={2} style={{ marginBottom: '32px', textAlign: 'center' }}>
                Profile Settings
            </Title>
            
            <Row justify="center">
                <Col xs={24} sm={24} md={22} lg={20} xl={18} xxl={16}>
                    <Card 
                        variant="outlined"
                        style={{ 
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                            borderRadius: '8px'
                        }}
                    >
                        <Form form={form} layout="vertical" onFinish={onFinish}>
                            <Title level={4} style={{ marginBottom: '24px', color: '#1890ff' }}>
                                Personal Information
                            </Title>
                            
                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item 
                                        label="First Name" 
                                        name="firstName" 
                                        rules={[{ required: true, message: 'Please enter your first name' }]}
                                    >
                                        <Input size="large" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item 
                                        label="Last Name" 
                                        name="lastName" 
                                        rules={[{ required: true, message: 'Please enter your last name' }]}
                                    >
                                        <Input size="large" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item 
                                label="Email Address" 
                                name="email" 
                                rules={[{ required: true, type: "email", message: 'Please enter a valid email address' }]}
                            >
                                <Input size="large" disabled style={{ backgroundColor: '#f5f5f5' }} />
                            </Form.Item>

                            <Form.Item label="Institution" name="institution">
                                <Input size="large" placeholder="Enter your institution or organization" />
                            </Form.Item>

                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Phone Number" name="phone">
                                        <Input size="large" placeholder="Enter your phone number" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Cell Phone" name="cell">
                                        <Input size="large" placeholder="Enter your cell phone" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Title level={4} style={{ marginTop: '32px', marginBottom: '24px', color: '#1890ff' }}>
                                Address
                            </Title>
                            
                            <Form.Item label="Mailing Address" name="address">
                                <AddressInput address={address} setAddress={setAddress} />
                            </Form.Item>

                            <Title level={4} style={{ marginTop: '32px', marginBottom: '24px', color: '#1890ff' }}>
                                Billing Information
                            </Title>
                            
                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Billing Contact Name">
                                        <Input 
                                            size="large" 
                                            placeholder="Enter billing contact name"
                                            value={billingAddress?.billingContactName || ""}
                                            onChange={(e) => setBillingAddress({
                                                ...(billingAddress || {}),
                                                billingContactName: e.target.value
                                            } as BillingAddressInterface)}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Billing Email">
                                        <Input 
                                            size="large" 
                                            type="email"
                                            placeholder="Enter billing email address"
                                            value={billingAddress?.billingEmail || ""}
                                            onChange={(e) => setBillingAddress({
                                                ...(billingAddress || {}),
                                                billingEmail: e.target.value
                                            } as BillingAddressInterface)}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            
                            <Form.Item label="Billing Address">
                                <AddressInput 
                                    address={billingAddress} 
                                    setAddress={(addr) => setBillingAddress({
                                        ...(billingAddress || {}),
                                        ...addr
                                    } as BillingAddressInterface)} 
                                />
                            </Form.Item>

                            <Form.Item style={{ marginTop: '32px', textAlign: 'center' }}>
                                <Button 
                                    type="primary" 
                                    htmlType="submit" 
                                    loading={saving}
                                    size="large"
                                    style={{ 
                                        minWidth: '120px',
                                        height: '40px',
                                        borderRadius: '6px',
                                        fontWeight: '500'
                                    }}
                                >
                                    {saving ? 'Saving...' : 'Save Profile'}
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>
            </Row>
        </ContainerFull>
    )
}

export default Profile
